// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "typeNames": ["NestedKeyOf"], "path": "../../../utils/types" }

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep, NestedKeyOf } from '../../../utils/types';
import Enums from '../Enums';
import {
  type FeatureFlag,
  type FeatureFlagWireFormat,
  NewFeatureFlag,
} from '../FeatureFlag/FeatureFlag';
import { NewTenant, Tenant, type TenantWireFormat } from '../Tenant/Tenant';
import { NewUser, User, type UserWireFormat } from '../User/User';
import {
  NewUserEmailDefault,
  UserEmailDefault,
  type UserEmailDefaultWireFormat,
} from '../UserEmailDefault/UserEmailDefault';
import {
  NewUserSetting,
  type UserSetting,
  type UserSettingWireFormat,
} from '../UserSetting/UserSetting';

export class UserInfo {
  readonly user: User;
  readonly tenant: Tenant;
  readonly settings: UserSetting[];
  readonly emailDefaults: UserEmailDefault;
  readonly featureFlags: FeatureFlag[];
  readonly productCategories: Enums.ProductCategory[];
  readonly mobileForceUpdateVersion: string;

  constructor(data: { [key: string]: any } = {}) {
    this.user = NewUser(data.user);
    this.tenant = NewTenant(data.tenant);
    this.settings = (data.settings ?? []).map((settings: any) => NewUserSetting(settings));
    this.emailDefaults = NewUserEmailDefault(data.emailDefaults);
    this.featureFlags = (data.featureFlags ?? []).map((featureFlags: any) =>
      NewFeatureFlag(featureFlags),
    );
    this.productCategories = (data.productCategories ?? []).map((productCategories: any) =>
      productCategories === undefined ? Enums.ProductCategory.ReadyMix : productCategories,
    );
    this.mobileForceUpdateVersion =
      data.mobileForceUpdateVersion === undefined ? '' : data.mobileForceUpdateVersion;
  }

  static zero(): UserInfo {
    const zeroValues: ExcludeMethodsDeep<UserInfo> = {
      user: User.zero(),
      tenant: Tenant.zero(),
      settings: [],
      emailDefaults: UserEmailDefault.zero(),
      featureFlags: [],
      productCategories: [],
      mobileForceUpdateVersion: '',
    };
    return new UserInfo(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /**
   * hasFlags will return true if the user's flags satisfy the requirements.
   *
   * If no flags are provided, it will return false. It assumes a developer mistake.
   *
   * If operator is `and`, all flags must be present.
   *
   * If operator is `or`, or if operator is not provided, any flag must be present.
   */
  hasFlags(flagNames: Enums.FeatureFlagName[], operator: 'and' | 'or' = 'or'): boolean {
    if (flagNames.length === 0) {
      return false;
    }

    if (operator === 'and') {
      return flagNames.every((flagName) =>
        this.featureFlags.some((f) => f.name === flagName && f.isEnabled),
      );
    }
    return this.featureFlags.some((flag) => flagNames.includes(flag.name) && flag.isEnabled);
  }

  /**
   * hasRoles returns true if the user's roles satisfy the requirements.
   *
   * - If no roles are provided, hasRoles assumes a developer mistake and returns false.
   * - If operator is 'and', returns true if the current user has all of the specified roles.
   * - If operator is 'or' (this is the default), returns true if the current user has one or more
   *   of the specified roles.
   */
  hasRoles(roleNames: Enums.RoleName[], operator: 'and' | 'or' = 'or'): boolean {
    if (roleNames.length === 0) {
      return false;
    }

    if (operator === 'and') {
      return roleNames.every((roleName) => this.user.roles.some((role) => role.name === roleName));
    }
    return this.user.roles.some((role) => roleNames.includes(role.name));
  }

  hasPermissions(requiredPermissions?: Enums.Permission[]): boolean {
    if (requiredPermissions === undefined || requiredPermissions.length === 0) return true;

    return this.user.permissions.some((permission) => requiredPermissions.includes(permission));
  }

  /**
   * userIfRoles returns the current User if that user has all of the specified roles
   * (according to the behavior of `hasRoles`), otherwise returning undefined.
   */
  userIfRoles(roleNames: Enums.RoleName[]): User | undefined {
    if (!this.hasRoles(roleNames, 'and')) {
      return undefined;
    }
    return this.user;
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type UserInfoWireFormat = {
  user: UserWireFormat;
  tenant: TenantWireFormat;
  settings: UserSettingWireFormat[];
  emailDefaults: UserEmailDefaultWireFormat;
  featureFlags: FeatureFlagWireFormat[];
  productCategories: string[];
  mobileForceUpdateVersion: string;
};

type NewUserInfoProps = PartialDeep<UserInfo, { recurseIntoArrays: true }>;
export const NewUserInfo: (props: NewUserInfoProps) => UserInfo = (props) => new UserInfo(props);

type NewUserInfoFromWireFormatProps = PartialDeep<UserInfoWireFormat, { recurseIntoArrays: true }>;
export const NewUserInfoFromWireFormat: (props: NewUserInfoFromWireFormatProps) => UserInfo = (
  props,
) => new UserInfo(props);
